import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiRequestError } from '../../api/client';
import { getSupportHours, setStoredSessionToken } from '../../api/web-chat';
import { Alert, Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ChatFloatingButton } from '../../components/chat/ChatFloatingButton';
import { Select } from '../../components/ui/Input';
import { useAuth } from '../../context/AuthContext';
import { useChatUi } from '../../context/ChatUiContext';

type PaymentMethod = {
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  status: string;
} | null;

type SupportServiceType = 'remote' | 'onsite';
type SupportTier = 'standard' | 'urgent';

type SupportLicense = {
  license_id: number;
  pos_name: string | null;
  business_name: string | null;
  business_address: string | null;
  business_postal_code: string | null;
  business_city: string | null;
  business_province: string | null;
  onsite_ready: boolean;
};

type ClientTicket = {
  id: number;
  subject: string | null;
  status: string;
  priority: string;
  service_type: SupportServiceType;
  billing_tier: SupportTier;
  accepted_hourly_rate_eur: number | string;
  license_id: number | null;
  whatsapp_conversation_id: string | null;
  source: 'web_panel' | 'whatsapp';
  customer_phone: string;
  billable_hours: number | string | null;
  client_charge_total: number | string | null;
  client_charge_status: string | null;
  proposal_expires_at: string | null;
  customer_decision_channel: 'panel' | 'app' | 'whatsapp' | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
};

type PendingCharge = {
  ticketId: number;
  subject: string | null;
  total: number;
  status: 'pending' | 'failed';
  resolvedAt: string | null;
};

type CreateRequestResponse = {
  success: boolean;
  ticketId: number;
  sessionToken: string;
  status: string;
  service_type: SupportServiceType;
  billing_tier: SupportTier;
  accepted_hourly_rate_eur?: number;
  alreadyActive?: boolean;
};

const ACTIVE_STATUSES = new Set(['awaiting_customer_approval', 'pending', 'assigned', 'in_progress']);

const STATUS_LABELS: Record<string, string> = {
  awaiting_customer_approval: 'Pendiente de autorización',
  pending: 'En cola',
  assigned: 'Agente asignado',
  in_progress: 'En curso',
  resolved: 'Resuelta',
  cancelled: 'Cancelada',
  closed: 'Cerrada',
};

function formatDate(value: string | null): string {
  if (!value) return '';
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatMoney(value: number | string | null | undefined): string {
  return `${Number(value ?? 0).toFixed(2).replace('.', ',')} €`;
}

function licenseLabel(license: SupportLicense): string {
  const name = license.pos_name || license.business_name || `Licencia #${license.license_id}`;
  const place = [license.business_postal_code, license.business_city].filter(Boolean).join(' ');
  return place ? `${name} · ${place}` : name;
}

function statusTone(status: string): 'info' | 'warning' | 'success' | 'neutral' {
  if (status === 'in_progress') return 'success';
  if (status === 'assigned') return 'warning';
  if (status === 'awaiting_customer_approval') return 'warning';
  if (status === 'pending') return 'info';
  return 'neutral';
}

export function SupportPage() {
  const { isDemo } = useAuth();
  const { openChat } = useChatUi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);
  const [licenses, setLicenses] = useState<SupportLicense[]>([]);
  const [tickets, setTickets] = useState<ClientTicket[]>([]);
  const [pendingCharges, setPendingCharges] = useState<PendingCharge[]>([]);
  const [outsideHours, setOutsideHours] = useState(false);
  const [serviceType, setServiceType] = useState<SupportServiceType>('remote');
  const [billingTier, setBillingTier] = useState<SupportTier>('standard');
  const [selectedLicenseId, setSelectedLicenseId] = useState<number | null>(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [priceAccepted, setPriceAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [savingCard, setSavingCard] = useState(false);
  const [payingChargeId, setPayingChargeId] = useState<number | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [decidingId, setDecidingId] = useState<number | null>(null);
  const [proposalAccepted, setProposalAccepted] = useState<Record<number, boolean>>({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadOverview = useCallback(async () => {
    const [payment, licenseData, ticketData, chargeData, hours] = await Promise.all([
      api.get<{ success: boolean; paymentMethod: PaymentMethod }>('/api/support/payment-method'),
      api.get<{ success: boolean; licenses: SupportLicense[] }>('/api/tickets/support-licenses'),
      api.get<{ success: boolean; tickets: ClientTicket[] }>('/api/tickets/client'),
      api.get<{ success: boolean; charges: PendingCharge[] }>('/api/support/pending-charges'),
      getSupportHours(),
    ]);
    setPaymentMethod(payment.paymentMethod);
    setLicenses(licenseData.licenses ?? []);
    setTickets(ticketData.tickets ?? []);
    setPendingCharges(chargeData.charges ?? []);
    setOutsideHours(hours.outsideHours);
  }, []);

  useEffect(() => {
    void loadOverview()
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [loadOverview]);

  useEffect(() => {
    const card = searchParams.get('card');
    const charge = searchParams.get('charge');
    if (card === 'ok') setMessage('Tarjeta guardada correctamente. Ya puedes solicitar soporte.');
    if (card === 'cancel') setError('Has cancelado el alta de tarjeta.');
    if (charge === 'ok') setMessage('Pago recibido. Gracias.');
    if (charge === 'cancel') setError('El pago se ha cancelado; puedes volver a intentarlo aquí.');
    if (card || charge) {
      setSearchParams({}, { replace: true });
      void loadOverview();
    }
  }, [loadOverview, searchParams, setSearchParams]);

  const hasCard = paymentMethod?.status === 'active' && Boolean(paymentMethod.last4);
  const onsiteLicenses = useMemo(() => licenses.filter((license) => license.onsite_ready), [licenses]);
  const activeTickets = useMemo(
    () => tickets.filter((ticket) => ACTIVE_STATUSES.has(ticket.status)),
    [tickets]
  );
  const ticketHistory = useMemo(
    () => tickets.filter((ticket) => !ACTIVE_STATUSES.has(ticket.status)).slice(0, 8),
    [tickets]
  );
  const selectedLicense = licenses.find((license) => license.license_id === selectedLicenseId) ?? null;
  const hourlyRate = billingTier === 'urgent' ? 55 : 35;
  const needsOnsiteLicense = serviceType === 'onsite';
  const canSubmit =
    !isDemo &&
    hasCard &&
    requestMessage.trim().length >= 5 &&
    priceAccepted &&
    (!needsOnsiteLicense || Boolean(selectedLicense?.onsite_ready));

  useEffect(() => {
    setPriceAccepted(false);
    if (serviceType === 'onsite') {
      setSelectedLicenseId((current) => {
        if (current && onsiteLicenses.some((license) => license.license_id === current)) return current;
        return onsiteLicenses.length === 1 ? onsiteLicenses[0].license_id : null;
      });
    }
  }, [serviceType, onsiteLicenses]);

  useEffect(() => {
    if (!outsideHours && billingTier === 'urgent') setBillingTier('standard');
  }, [billingTier, outsideHours]);

  function openTicket(ticket: ClientTicket | CreateRequestResponse) {
    const isStoredTicket = 'whatsapp_conversation_id' in ticket;
    const sessionToken = isStoredTicket ? ticket.whatsapp_conversation_id : ticket.sessionToken;
    if (!sessionToken) return;
    setStoredSessionToken(sessionToken);
    setSearchParams({ chat: 'open', ticket: String(isStoredTicket ? ticket.id : ticket.ticketId) });
    openChat();
  }

  async function addCard() {
    if (isDemo || savingCard) return;
    setError('');
    setSavingCard(true);
    try {
      const result = await api.post<{ success: boolean; url?: string; error?: string }>(
        '/api/support/payment-method/setup'
      );
      if (!result.url) throw new Error(result.error || 'No se pudo iniciar el alta de tarjeta');
      window.location.assign(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo añadir la tarjeta');
      setSavingCard(false);
    }
  }

  async function createRequest() {
    if (!canSubmit || requesting) return;
    setError('');
    setMessage('');
    setRequesting(true);
    try {
      const result = await api.post<CreateRequestResponse>('/api/tickets/request', {
        service_type: serviceType,
        billing_tier: billingTier,
        license_id: selectedLicenseId ?? undefined,
        message: requestMessage.trim(),
      });
      setRequestMessage('');
      setPriceAccepted(false);
      await loadOverview();
      if (result.alreadyActive) {
        setMessage('Ya tenías una solicitud similar activa. Hemos abierto su conversación.');
      }
      openTicket(result);
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'needs_card') {
        setError('Añade una tarjeta antes de solicitar soporte.');
      } else {
        setError(err instanceof Error ? err.message : 'No se pudo crear la solicitud');
      }
    } finally {
      setRequesting(false);
    }
  }

  async function cancelTicket(ticketId: number) {
    if (cancellingId) return;
    setCancellingId(ticketId);
    setError('');
    try {
      await api.post(`/api/tickets/client/${ticketId}/cancel`);
      setMessage('Solicitud cancelada.');
      await loadOverview();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cancelar la solicitud');
    } finally {
      setCancellingId(null);
    }
  }

  async function decideProposal(ticketId: number, approved: boolean) {
    if (decidingId) return;
    setDecidingId(ticketId);
    setError('');
    setMessage('');
    try {
      await api.post(`/api/tickets/client/${ticketId}/proposal-decision`, { approved, approval_channel: 'panel' });
      setMessage(approved
        ? 'Propuesta autorizada. El agente ya puede comenzar el servicio.'
        : 'Propuesta rechazada. No se realizará ningún cargo.');
      await loadOverview();
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'needs_card') {
        setError('Añade una tarjeta antes de autorizar. No se cobrará nada hasta finalizar el servicio.');
      } else {
        setError(err instanceof Error ? err.message : 'No se pudo actualizar la propuesta');
      }
    } finally {
      setDecidingId(null);
    }
  }

  async function payPendingCharge(ticketId: number) {
    if (payingChargeId) return;
    setPayingChargeId(ticketId);
    setError('');
    try {
      const result = await api.post<{ success: boolean; url?: string; error?: string }>(
        `/api/tickets/${ticketId}/charge-link`
      );
      if (!result.url) throw new Error(result.error || 'No se pudo generar el enlace de pago');
      window.location.assign(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo iniciar el pago');
      setPayingChargeId(null);
    }
  }

  if (loading) return <p className="muted">Cargando soporte…</p>;

  return (
    <div className="panel-support-page panel-support-page--simplified">
      <div className="panel-page-header">
        <div>
          <h1 className="page-title">Soporte técnico</h1>
          <p className="page-subtitle">Una solicitud, una conversación y un único cobro al finalizar.</p>
        </div>
        <a href="/docs/TPV-Hosty-Manual-de-usuario.pdf" target="_blank" rel="noopener noreferrer">
          <Button type="button" variant="secondary" size="sm">Abrir manual</Button>
        </a>
      </div>

      {message ? <Alert tone="success">{message}</Alert> : null}
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {isDemo ? <Alert>La cuenta de demostración no puede crear solicitudes ni guardar tarjetas.</Alert> : null}

      {activeTickets.length > 0 ? (
        <Card title="Solicitudes activas" subtitle="Continúa la conversación o consulta su estado.">
          <div className="support-request-list">
            {activeTickets.map((ticket) => (
              <article className="support-request-row" key={ticket.id}>
                <div className="support-request-row__content">
                  <div className="support-request-row__badges">
                    <Badge tone={statusTone(ticket.status)}>{STATUS_LABELS[ticket.status] || ticket.status}</Badge>
                    <Badge>{ticket.service_type === 'onsite' ? 'Presencial' : 'Remoto'}</Badge>
                    <Badge tone={ticket.billing_tier === 'urgent' ? 'danger' : 'neutral'}>
                      {ticket.billing_tier === 'urgent' ? '55 €/h urgente' : '35 €/h estándar'}
                    </Badge>
                  </div>
                  <strong>{ticket.subject || `Solicitud #${ticket.id}`}</strong>
                  <span className="muted">Actualizada {formatDate(ticket.updated_at)}</span>
                  {ticket.status === 'awaiting_customer_approval' ? (
                    <>
                    <span className="muted">Válida hasta {formatDate(ticket.proposal_expires_at)}</span>
                    <label className="support-proposal-confirmation">
                      <input
                        type="checkbox"
                        checked={Boolean(proposalAccepted[ticket.id])}
                        onChange={(event) => setProposalAccepted((current) => ({
                          ...current,
                          [ticket.id]: event.target.checked,
                        }))}
                      />
                      <span>
                        Autorizo {formatMoney(ticket.accepted_hourly_rate_eur)}/h, IVA incluido
                        {ticket.service_type === 'onsite'
                          ? ', más el desplazamiento calculado al finalizar'
                          : ''}. No se cobra nada ahora.
                      </span>
                    </label>
                    </>
                  ) : null}
                </div>
                <div className="support-request-row__actions">
                  {ticket.status === 'pending' ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={cancellingId !== null}
                      loading={cancellingId === ticket.id}
                      onClick={() => void cancelTicket(ticket.id)}
                    >
                      Cancelar
                    </Button>
                  ) : null}
                  {ticket.status === 'awaiting_customer_approval' ? (
                    <>
                      {!hasCard ? (
                        <Button type="button" variant="secondary" size="sm" loading={savingCard} onClick={() => void addCard()}>
                          Añadir tarjeta
                        </Button>
                      ) : null}
                      <Button type="button" variant="ghost" size="sm" disabled={decidingId !== null} onClick={() => void decideProposal(ticket.id, false)}>
                        Rechazar
                      </Button>
                      <Button type="button" size="sm" loading={decidingId === ticket.id} disabled={!proposalAccepted[ticket.id] || !hasCard} onClick={() => void decideProposal(ticket.id, true)}>
                        Autorizar servicio
                      </Button>
                    </>
                  ) : ticket.source === 'whatsapp' ? (
                    <span className="muted">La conversación continúa por WhatsApp.</span>
                  ) : (
                    <Button type="button" size="sm" onClick={() => openTicket(ticket)}>Abrir conversación</Button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </Card>
      ) : null}

      <Card title="Nueva solicitud" subtitle="Describe lo que necesitas y elige cómo quieres que te atendamos.">
        <div className="support-request-form">
          <fieldset className="support-choice-group">
            <legend>Tipo de servicio</legend>
            <label className={`support-choice${serviceType === 'remote' ? ' is-selected' : ''}`}>
              <input
                type="radio"
                name="service-type"
                value="remote"
                checked={serviceType === 'remote'}
                onChange={() => setServiceType('remote')}
              />
              <span><strong>Soporte remoto</strong><small>Incidencias, configuración, formación o instalación sin desplazamiento.</small></span>
            </label>
            <label className={`support-choice${serviceType === 'onsite' ? ' is-selected' : ''}`}>
              <input
                type="radio"
                name="service-type"
                value="onsite"
                checked={serviceType === 'onsite'}
                onChange={() => setServiceType('onsite')}
              />
              <span><strong>Visita presencial</strong><small>Un técnico se desplaza al local. Se añadirá el coste de desplazamiento.</small></span>
            </label>
          </fieldset>

          {serviceType === 'onsite' ? (
            onsiteLicenses.length > 0 ? (
              <Select
                label="Local que necesita la visita"
                value={selectedLicenseId == null ? '' : String(selectedLicenseId)}
                onChange={(event) => setSelectedLicenseId(Number(event.target.value) || null)}
                options={[
                  { value: '', label: 'Selecciona un local…' },
                  ...onsiteLicenses.map((license) => ({
                    value: String(license.license_id),
                    label: licenseLabel(license),
                  })),
                ]}
              />
            ) : (
              <Alert tone="warning">
                Para pedir una visita, completa el nombre, la dirección y el código postal de una licencia vigente en{' '}
                <Link to="/panel/licencias">Licencias</Link>.
              </Alert>
            )
          ) : licenses.length > 0 ? (
            <Select
              label="Local relacionado (opcional)"
              value={selectedLicenseId == null ? '' : String(selectedLicenseId)}
              onChange={(event) => setSelectedLicenseId(Number(event.target.value) || null)}
              options={[
                { value: '', label: 'Consulta general' },
                ...licenses.map((license) => ({ value: String(license.license_id), label: licenseLabel(license) })),
              ]}
            />
          ) : null}

          <label className="ui-field" htmlFor="support-request-message">
            <span className="ui-field__label">¿Qué necesitas?</span>
            <textarea
              id="support-request-message"
              className="ui-textarea"
              rows={5}
              maxLength={3000}
              value={requestMessage}
              onChange={(event) => setRequestMessage(event.target.value)}
              placeholder="Describe el problema, cuándo ocurre y cualquier dato que pueda ayudarnos…"
            />
          </label>

          <fieldset className="support-choice-group support-choice-group--tier">
            <legend>Tarifa</legend>
            <label className={`support-choice${billingTier === 'standard' ? ' is-selected' : ''}`}>
              <input
                type="radio"
                name="billing-tier"
                value="standard"
                checked={billingTier === 'standard'}
                onChange={() => setBillingTier('standard')}
              />
              <span><strong>Estándar · 35 €/h</strong><small>Lunes a viernes, de 9:00 a 18:00.</small></span>
            </label>
            {outsideHours ? (
              <label className={`support-choice support-choice--urgent${billingTier === 'urgent' ? ' is-selected' : ''}`}>
                <input
                  type="radio"
                  name="billing-tier"
                  value="urgent"
                  checked={billingTier === 'urgent'}
                  onChange={() => setBillingTier('urgent')}
                />
                <span><strong>Urgente · 55 €/h</strong><small>Solo para incidencias que no pueden esperar al próximo horario.</small></span>
              </label>
            ) : null}
          </fieldset>

          {!hasCard ? (
            <Alert tone="warning">
              Añade una tarjeta antes de enviar la solicitud. No se cobrará nada ahora.
              <div className="support-inline-action">
                <Button type="button" size="sm" loading={savingCard} disabled={isDemo} onClick={() => void addCard()}>
                  Añadir tarjeta
                </Button>
              </div>
            </Alert>
          ) : (
            <div className="support-payment-summary">
              <span>Pago al finalizar</span>
              <strong>{(paymentMethod?.brand || 'Tarjeta').toUpperCase()} ···· {paymentMethod?.last4}</strong>
              <Button type="button" variant="ghost" size="sm" onClick={() => void addCard()} disabled={isDemo}>
                Cambiar
              </Button>
            </div>
          )}

          <label className="support-price-confirmation">
            <input
              type="checkbox"
              checked={priceAccepted}
              onChange={(event) => setPriceAccepted(event.target.checked)}
            />
            <span>
              Confirmo la tarifa de <strong>{hourlyRate} €/h, IVA incluido</strong>
              {serviceType === 'onsite' ? ', más el desplazamiento indicado al finalizar' : ''}.
            </span>
          </label>

          <Button
            type="button"
            loading={requesting}
            disabled={!canSubmit || requesting}
            onClick={() => void createRequest()}
          >
            Enviar solicitud
          </Button>
        </div>
      </Card>

      {pendingCharges.length > 0 ? (
        <Card title="Cobros pendientes">
          <div className="support-request-list">
            {pendingCharges.map((charge) => (
              <article className="support-request-row" key={charge.ticketId}>
                <div className="support-request-row__content">
                  <Badge tone={charge.status === 'failed' ? 'danger' : 'warning'}>
                    {charge.status === 'failed' ? 'Tarjeta rechazada' : 'Pendiente'}
                  </Badge>
                  <strong>{charge.subject || `Servicio #${charge.ticketId}`}</strong>
                  <span className="muted">{formatMoney(charge.total)}</span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  loading={payingChargeId === charge.ticketId}
                  disabled={payingChargeId !== null}
                  onClick={() => void payPendingCharge(charge.ticketId)}
                >
                  Pagar ahora
                </Button>
              </article>
            ))}
          </div>
        </Card>
      ) : null}

      {ticketHistory.length > 0 ? (
        <Card title="Historial reciente">
          <div className="support-request-list support-request-list--history">
            {ticketHistory.map((ticket) => (
              <article className="support-request-row" key={ticket.id}>
                <div className="support-request-row__content">
                  <div className="support-request-row__badges">
                    <Badge>{STATUS_LABELS[ticket.status] || ticket.status}</Badge>
                    <Badge>{ticket.service_type === 'onsite' ? 'Presencial' : 'Remoto'}</Badge>
                  </div>
                  <strong>{ticket.subject || `Solicitud #${ticket.id}`}</strong>
                  <span className="muted">
                    {formatDate(ticket.resolved_at || ticket.updated_at)}
                    {ticket.client_charge_total != null ? ` · ${formatMoney(ticket.client_charge_total)}` : ''}
                  </span>
                </div>
                {ticket.status === 'resolved' && ticket.source !== 'whatsapp' ? (
                  <Button type="button" variant="secondary" size="sm" onClick={() => openTicket(ticket)}>
                    Ver conversación
                  </Button>
                ) : ticket.source === 'whatsapp' ? (
                  <span className="muted">Conversación por WhatsApp</span>
                ) : null}
              </article>
            ))}
          </div>
        </Card>
      ) : null}
      <ChatFloatingButton />
    </div>
  );
}
